import { MonitoredOperationListItemResponse } from '@6grain/masofa-monolith-api-client/common/model/monitoredOperationListItemResponse';
import { ChartConfig, ChartData, ChartDataset } from '@components/svg-chart';
import { assignmentRowTitle } from '../orchestration-identity.util';

/** One sample as returned on the worker card. */
export interface WorkerChartSample {
  sampledAt: string;
  workingSetBytes: number;
}

const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
const MIB = 1024 * 1024;

/** How many stacked lanes the strip above the RSS plot will draw. */
export const WORKER_CHART_SPAN_LANE_CAP = 8;
export const ASSIGNMENT_LANE_HEIGHT = 12;
export const ASSIGNMENT_LANE_GAP = 4;
export const ASSIGNMENT_STRIP_GAP = 6;
/** Horizontal split so a handoff's diamond and the next triangle do not share a pixel. */
export const ASSIGNMENT_CAP_SPLIT_PX = 7;

/** Worker-card chart and assignment-list window. Default `1h` is omitted from the URL. */
export const WORKER_CHART_WINDOWS = ['15m', '1h', '3h', '6h', '12h', '24h'] as const;

export type WorkerChartWindow = (typeof WORKER_CHART_WINDOWS)[number];

export const DEFAULT_WORKER_CHART_WINDOW: WorkerChartWindow = '1h';

const WORKER_CHART_WINDOW_MS: Record<WorkerChartWindow, number> = {
  '15m': 15 * MINUTE_MS,
  '1h': HOUR_MS,
  '3h': 3 * HOUR_MS,
  '6h': 6 * HOUR_MS,
  '12h': 12 * HOUR_MS,
  '24h': 24 * HOUR_MS,
};

export const WORKER_CHART_WINDOW_CHIPS: { id: WorkerChartWindow; label: string }[] = [
  { id: '15m', label: '15 minutes' },
  { id: '1h', label: '1 hour' },
  { id: '3h', label: '3 hours' },
  { id: '6h', label: '6 hours' },
  { id: '12h', label: '12 hours' },
  { id: '24h', label: '24 hours' },
];

/** Unknown or empty values fall back to the last hour. */
export function parseWorkerChartWindow(value: string | null | undefined): WorkerChartWindow {
  const raw = value?.trim() ?? '';
  return (WORKER_CHART_WINDOWS as readonly string[]).includes(raw)
    ? raw as WorkerChartWindow
    : DEFAULT_WORKER_CHART_WINDOW;
}

/** `null` means the default and is left out of the URL. */
export function serializeWorkerChartWindow(chartWindow: WorkerChartWindow): string | null {
  return chartWindow === DEFAULT_WORKER_CHART_WINDOW ? null : chartWindow;
}

/** Inclusive `[now − duration, now]`. */
export function workerChartWindowBounds(chartWindow: WorkerChartWindow, now: Date): { from: Date; to: Date } {
  return {
    from: new Date(now.getTime() - WORKER_CHART_WINDOW_MS[chartWindow]),
    to: now,
  };
}

/** Availability ignores the chart-window chip and always shows this span. */
export const WORKER_AVAILABILITY_WINDOW: WorkerChartWindow = '24h';

/** ISO bounds the worker card sends as `activityFrom` / `activityTo`. */
export function workerChartActivityBounds(
  chartWindow: WorkerChartWindow,
  now: Date,
): { activityFrom: string; activityTo: string } {
  const bounds = workerChartWindowBounds(chartWindow, now);
  return {
    activityFrom: bounds.from.toISOString(),
    activityTo: bounds.to.toISOString(),
  };
}

/** Samples whose `sampledAt` falls inside the chart window, inclusive. */
export function samplesInWorkerChartWindow(
  samples: WorkerChartSample[],
  chartWindow: WorkerChartWindow,
  now: Date,
): WorkerChartSample[] {
  const bounds = workerChartWindowBounds(chartWindow, now);
  const from = bounds.from.getTime();
  const to = bounds.to.getTime();
  return samples.filter(sample => {
    const at = Date.parse(sample.sampledAt);
    return Number.isFinite(at) && at >= from && at <= to;
  });
}

/** Newest assignments the RSS chart asks the operations feed for. The feed caps a page at 200. */
export const WORKER_CHART_ASSIGNMENT_PAGE_SIZE = 200;

/** One start or completion from the operations page, before it is paired into a span. */
export interface WorkerChartMark {
  id: string;
  at: string;
  kind: 'start' | 'complete';
  title: string;
  processType: string;
}

/** One assignment run drawn above the RSS plot. `lane` is null when the strip cap hid it. */
export interface WorkerChartSpan {
  id: string;
  title: string;
  processType: string;
  startIndex: number;
  endIndex: number;
  startCapped: boolean;
  endCapped: boolean;
  lane: number | null;
}

/** Pixel rect of one span bar. Cap centers are null when that end has no marker. */
export interface AssignmentSpanBar {
  span: WorkerChartSpan;
  x: number;
  y: number;
  width: number;
  height: number;
  startX: number | null;
  endX: number | null;
}

export interface WorkerMemoryChartData extends ChartData {
  assignmentSpans: WorkerChartSpan[];
}

/** Hourly up/down bars from the earliest sample through the current UTC hour. */
export function buildWorkerAvailabilityChart(samples: WorkerChartSample[], now: Date): ChartData {
  const times = sampleTimes(samples);
  if (times.length === 0) {
    return emptyChart();
  }
  const currentHour = utcHour(now.getTime());
  const firstHour = utcHour(Math.min(...times));
  if (firstHour > currentHour) {
    return emptyChart();
  }
  const labels: string[] = [];
  const data: number[] = [];
  for (let hour = firstHour; hour <= currentHour; hour += HOUR_MS) {
    const next = hour + HOUR_MS;
    labels.push(`${pad(new Date(hour).getUTCHours())}:00`);
    data.push(times.some(time => time >= hour && time < next) ? 1 : 0);
  }
  return {
    labels,
    datasets: [{
      label: 'Online',
      data,
      borderColor: '#2e7d32',
      backgroundColor: 'rgba(46, 125, 50, 0.55)',
    }],
  };
}

/** One point per sample, working set in mebibytes. Assignment runs are spans, not extra points. */
export function buildWorkerMemoryChart(samples: WorkerChartSample[], marks: WorkerChartMark[] = []): WorkerMemoryChartData {
  const points = samples
    .map(sample => ({ at: Date.parse(sample.sampledAt), bytes: sample.workingSetBytes }))
    .filter(point => Number.isFinite(point.at))
    .sort((left, right) => left.at - right.at);
  if (points.length === 0) {
    return { labels: [], datasets: [], assignmentSpans: [] };
  }
  const times = points.map(point => point.at);
  return {
    labels: points.map(point => {
      const date = new Date(point.at);
      return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
    }),
    datasets: [{
      label: 'RSS',
      data: points.map(point => roundMib(point.bytes / MIB)),
      borderColor: '#1565c0',
      backgroundColor: 'rgba(21, 101, 192, 0.15)',
      pointRadius: 0,
      pointHoverRadius: 0,
      pointHitRadius: 0,
    }],
    assignmentSpans: buildAssignmentSpans(times, marks),
  };
}

/** Start and completion instants from one operations page. Rows without an id are skipped. */
export function assignmentChartMarks(items: MonitoredOperationListItemResponse[]): WorkerChartMark[] {
  const marks: WorkerChartMark[] = [];
  for (const item of items) {
    const id = item.id?.trim() ?? '';
    if (!id) {
      continue;
    }
    const processType = item.operationType?.trim() ?? '';
    const title = assignmentRowTitle(item.name, processType);
    if (item.startedAt) {
      marks.push({ id, at: item.startedAt, kind: 'start', title, processType });
    }
    if (item.completedAt) {
      marks.push({ id, at: item.completedAt, kind: 'complete', title, processType });
    }
  }
  return marks;
}

/** Pair start and completion marks onto the sample index range. Times outside the samples lose their cap. */
export function buildAssignmentSpans(sampleTimes: number[], marks: WorkerChartMark[]): WorkerChartSpan[] {
  if (sampleTimes.length === 0) {
    return [];
  }
  const groups = new Map<string, { title: string; processType: string; starts: number[]; completes: number[] }>();
  for (const mark of marks) {
    const at = Date.parse(mark.at);
    if (!Number.isFinite(at)) {
      continue;
    }
    const group = groups.get(mark.id) ?? { title: mark.title, processType: mark.processType, starts: [], completes: [] };
    if (!group.title) {
      group.title = mark.title;
    }
    if (!group.processType) {
      group.processType = mark.processType;
    }
    if (mark.kind === 'start') {
      group.starts.push(at);
    } else {
      group.completes.push(at);
    }
    groups.set(mark.id, group);
  }
  const first = sampleTimes[0];
  const last = sampleTimes[sampleTimes.length - 1];
  const lastIndex = sampleTimes.length - 1;
  const spans: WorkerChartSpan[] = [];
  for (const [id, group] of groups) {
    const startAt = group.starts.length ? Math.min(...group.starts) : null;
    const endAt = group.completes.length ? Math.max(...group.completes) : null;
    const startInside = startAt != null && startAt >= first && startAt <= last;
    const endInside = endAt != null && endAt >= first && endAt <= last;
    if (!startInside && !endInside) {
      continue;
    }
    const startIndex = startInside && startAt != null ? nearestIndex(sampleTimes, startAt) : 0;
    const endIndex = endInside && endAt != null ? nearestIndex(sampleTimes, endAt) : lastIndex;
    spans.push({
      id,
      title: group.title,
      processType: group.processType,
      startIndex,
      endIndex: Math.max(startIndex, endIndex),
      startCapped: startInside,
      endCapped: endInside,
      lane: null,
    });
  }
  spans.sort((left, right) =>
    left.startIndex - right.startIndex || left.endIndex - right.endIndex || left.id.localeCompare(right.id));
  assignSpanLanes(spans);
  return spans;
}

/** Pixels reserved above the plot for the lanes that are actually drawn. */
export function assignmentStripPadding(spans: readonly { lane: number | null }[]): number {
  let maxLane = -1;
  for (const span of spans) {
    if (span.lane != null && span.lane > maxLane) {
      maxLane = span.lane;
    }
  }
  const lanes = maxLane + 1;
  return lanes === 0 ? 0 : ASSIGNMENT_STRIP_GAP + lanes * (ASSIGNMENT_LANE_HEIGHT + ASSIGNMENT_LANE_GAP);
}

/** Bar rectangles in chart pixels. `plotTop` is the top of the RSS plot. */
export function layoutAssignmentSpanBars(
  spans: readonly WorkerChartSpan[],
  xAt: (index: number) => number,
  plotTop: number,
): AssignmentSpanBar[] {
  const drawn = spans.filter(span => span.lane != null);
  const startCounts = new Map<number, number>();
  const endCounts = new Map<number, number>();
  for (const span of drawn) {
    if (span.startCapped) {
      startCounts.set(span.startIndex, (startCounts.get(span.startIndex) ?? 0) + 1);
    }
    if (span.endCapped) {
      endCounts.set(span.endIndex, (endCounts.get(span.endIndex) ?? 0) + 1);
    }
  }
  return drawn.map(span => {
    const lane = span.lane ?? 0;
    const y = plotTop - ASSIGNMENT_STRIP_GAP - ASSIGNMENT_LANE_HEIGHT
      - lane * (ASSIGNMENT_LANE_HEIGHT + ASSIGNMENT_LANE_GAP);
    let startX: number | null = null;
    let endX: number | null = null;
    if (span.startCapped && span.endCapped && span.startIndex === span.endIndex) {
      const x = xAt(span.startIndex);
      startX = x - ASSIGNMENT_CAP_SPLIT_PX;
      endX = x + ASSIGNMENT_CAP_SPLIT_PX;
    } else {
      if (span.startCapped) {
        const x = xAt(span.startIndex);
        startX = (endCounts.get(span.startIndex) ?? 0) > 0 ? x + ASSIGNMENT_CAP_SPLIT_PX : x;
      }
      if (span.endCapped) {
        const x = xAt(span.endIndex);
        endX = (startCounts.get(span.endIndex) ?? 0) > 0 ? x - ASSIGNMENT_CAP_SPLIT_PX : x;
      }
    }
    let left = span.startCapped ? startX ?? xAt(span.startIndex) : xAt(span.startIndex);
    let right = span.endCapped ? endX ?? xAt(span.endIndex) : xAt(span.endIndex);
    if (right < left) {
      const swap = left;
      left = right;
      right = swap;
    }
    if (right - left < ASSIGNMENT_CAP_SPLIT_PX * 2) {
      const mid = (left + right) / 2;
      left = mid - ASSIGNMENT_CAP_SPLIT_PX;
      right = mid + ASSIGNMENT_CAP_SPLIT_PX;
    }
    return { span, x: left, y, width: right - left, height: ASSIGNMENT_LANE_HEIGHT, startX, endX };
  });
}

/** Top-most bar under the pointer wins. The hit box includes the caps. */
export function assignmentSpanBarAt(bars: readonly AssignmentSpanBar[], x: number, y: number): AssignmentSpanBar | null {
  for (let index = bars.length - 1; index >= 0; index -= 1) {
    const bar = bars[index];
    if (x >= bar.x - 6 && x <= bar.x + bar.width + 6 && y >= bar.y - 4 && y <= bar.y + bar.height + 4) {
      return bar;
    }
  }
  return null;
}

/** Sample times of the caps. A missing cap is left off that side. */
export function assignmentSpanRange(labels: readonly string[], span: WorkerChartSpan): string {
  const start = labels[span.startIndex] ?? '';
  const end = labels[span.endIndex] ?? '';
  if (span.startCapped && span.endCapped) {
    return start === end ? start : `${start} – ${end}`;
  }
  if (span.startCapped) {
    return start ? `${start} –` : '';
  }
  if (span.endCapped) {
    return end ? `– ${end}` : '';
  }
  return '';
}

/** Process key shown under the name. Omitted when it repeats the name. */
export function assignmentSpanDetail(title: string, processType: string): string {
  const type = processType.trim();
  const name = title.trim();
  if (!type || type.toLowerCase() === name.toLowerCase()) {
    return '';
  }
  return type;
}

/** Tooltip title: assignment name, or the sample time for the RSS line. */
export function workerMemoryTooltipTitle(items: Array<{ dataset: ChartDataset; label?: string }>): string {
  const dataset = items[0]?.dataset;
  if (dataset?.assignmentId) {
    return dataset.assignmentTitle || dataset.processType || '';
  }
  return items[0]?.label ?? '';
}

/** Tooltip body: the process key, or the RSS value. A key already used as the title is omitted. */
export function workerMemoryTooltipLabel(item: { dataset: ChartDataset; formattedValue?: string }): string | string[] {
  const dataset = item.dataset;
  if (!dataset?.assignmentId) {
    const value = item.formattedValue ?? '';
    return value ? `RSS: ${value}` : 'RSS';
  }
  const type = dataset.processType?.trim() ?? '';
  const title = dataset.assignmentTitle?.trim() ?? '';
  if (!type || type.toLowerCase() === title.toLowerCase()) {
    return [];
  }
  return type;
}

/** Availability axis stays on 0 and 1. */
export function workerAvailabilityChartConfig(): ChartConfig {
  return {
    scales: {
      y: { beginAtZero: true, max: 1, ticks: { stepSize: 1 } },
      x: { ticks: { autoSkip: true, maxRotation: 0, maxTicksLimit: 6 } },
    },
  };
}

/** Memory axis fits the samples. Assignment spans are drawn above the plot, not on this scale. */
export function workerMemoryChartConfig(): ChartConfig {
  return {
    yAxisUnit: 'MiB',
    layout: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
    scales: {
      y: { grace: '8%' },
      x: { ticks: { autoSkip: true, maxRotation: 0, maxTicksLimit: 8 } },
    },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        filter: item => !assignmentSpanHovering(item.chart),
        callbacks: {
          title: items => workerMemoryTooltipTitle(items),
          label: item => workerMemoryTooltipLabel(item),
        },
      },
    },
  };
}

/** True when the value is a heartbeat row id the card can open. */
export function isWorkerCardId(id: string | null | undefined): boolean {
  const value = id?.trim() ?? '';
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
    && value !== '00000000-0000-0000-0000-000000000000';
}

function sampleTimes(samples: WorkerChartSample[]): number[] {
  return samples
    .map(sample => Date.parse(sample.sampledAt))
    .filter(time => Number.isFinite(time));
}

function utcHour(epoch: number): number {
  const date = new Date(epoch);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours());
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function roundMib(value: number): number {
  return Math.round(value * 10) / 10;
}

function nearestIndex(times: number[], at: number): number {
  let best = 0;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (let index = 0; index < times.length; index += 1) {
    const delta = Math.abs(times[index] - at);
    if (delta < bestDelta) {
      best = index;
      bestDelta = delta;
    }
  }
  return best;
}

/** A lane is free when the previous run ended before this one starts, including a shared handoff sample. */
function assignSpanLanes(spans: WorkerChartSpan[]): void {
  const laneEnds: number[] = [];
  for (const span of spans) {
    const lane = laneEnds.findIndex(end => end <= span.startIndex && end < span.endIndex);
    const assigned = lane === -1 ? laneEnds.length : lane;
    if (lane === -1) {
      laneEnds.push(span.endIndex);
    } else {
      laneEnds[lane] = span.endIndex;
    }
    span.lane = assigned < WORKER_CHART_SPAN_LANE_CAP ? assigned : null;
  }
}

const spanHovering = new WeakMap<object, boolean>();

/** The RSS tooltip hides while the pointer is on an assignment bar. */
export function setAssignmentSpanHover(chart: object, hovering: boolean): void {
  spanHovering.set(chart, hovering);
}

export function assignmentSpanHovering(chart: object | undefined): boolean {
  return !!chart && spanHovering.get(chart) === true;
}

function emptyChart(): ChartData {
  return { labels: [], datasets: [] };
}
